/**
 * The fixed asset register.
 *
 * Net book value is not stored as a typed-in figure: it is cost less
 * accumulated depreciation, and the accumulated depreciation is what the
 * depreciation runs have actually posted. A register whose net book values were
 * entered by hand does not survive its first depreciation run.
 *
 * Grant-funded assets carry the deferred grant alongside, released in step with
 * the depreciation — so the income and the charge move together, which is the
 * whole point of deferring the grant in the first place.
 */

import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useTerms } from '../lib/tenant';
import { date, money, statusLabel, statusTone, type BadgeTone } from '../lib/format';
import { AsyncPanel, Badge, Card, DataTable, Money, PageHeader, Stat, Tabs } from '../components/ui';

/**
 * One row of GET /assets.
 *
 * The money columns are whole numbers of pence here, not strings: that route
 * converts every BIGINT on its way out. The grant register further down
 * converts only its grant columns, so its cost arrives differently — see
 * GrantAssetRow.
 */
interface AssetRow {
  id: string;
  asset_no: string;
  name: string;
  category_name: string;
  location_name: string | null;
  cost_centre_code: string | null;
  acquisition_date: string;
  current_cost_minor: number;
  accumulated_depreciation_minor: number;
  net_book_value_minor: number;
  depreciation_method: string;
  status: string;
  is_grant_funded: boolean;
  remaining_life_months: number | null;
  last_verified_date: string | null;
}

interface AssetsResponse {
  assets: AssetRow[];
  byCategory: Array<{ category: string; count: number; costMinor: number; nbvMinor: number }>;
  totals: { count: number; costMinor: number; depreciationMinor: number; nbvMinor: number };
}

/**
 * One row of GET /assets/grant-register.
 *
 * `grant_remaining_minor` is the server's own subtraction, so the deferred
 * balance on screen is the same arithmetic that produced the total beneath it.
 * `current_cost_minor` is the one column that route does not convert, so it
 * arrives as a number or a string depending on the database driver and is read
 * through Number() at the point of use.
 */
interface GrantAssetRow {
  id: string;
  asset_no: string;
  name: string;
  category_name: string;
  grant_funder: string | null;
  grant_amount_minor: number;
  grant_released_minor: number;
  grant_remaining_minor: number;
  current_cost_minor: number | string;
}

interface GrantRegisterResponse {
  assets: GrantAssetRow[];
  totals: { awardedMinor: number; releasedMinor: number; remainingMinor: number };
}

/**
 * Badge tone for an asset status.
 *
 * statusTone() knows document workflow states — approved, posted, on hold — and
 * has no opinion about 'in_use' or 'held_for_disposal', so without this map
 * every row of the register wore the same neutral grey and the status column
 * carried no signal at all. The register's own vocabulary is mapped here, and
 * anything unexpected still falls through to the shared helper.
 */
const STATUS_TONES: Record<string, BadgeTone> = {
  in_use: 'ok',
  under_construction: 'info',
  fully_depreciated: 'neutral',
  impaired: 'warn',
  held_for_disposal: 'warn',
  disposed: 'neutral',
  transferred_out: 'neutral',
  written_off: 'error',
};

/**
 * Remaining useful life in the words a finance function actually uses.
 *
 * The register stores months because depreciation is charged monthly, but
 * nobody describes a building as having 504 months left. Anything shorter than
 * two years stays in months, where the month is the meaningful unit.
 */
function lifeLeft(months: number): string {
  if (months < 24) return `${months} month${months === 1 ? '' : 's'}`;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return rest ? `${years} yrs ${rest} ${rest === 1 ? 'mth' : 'mths'}` : `${years} yrs`;
}

export function AssetsPage() {
  const [tab, setTab] = useState('register');

  return (
    <>
      <PageHeader
        title="Fixed assets"
        subtitle="Cost, depreciation and net book value, computed rather than entered."
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'register', label: 'Register' },
          { key: 'grants', label: 'Deferred capital grants' },
        ]}
      />

      {/*
        Each tab is its own component so that its request starts as the tab
        opens. A single loader shared between the two showed the empty state for
        a frame on the way in, which reads as "there is nothing here" rather
        than "this is loading".
      */}
      {tab === 'register' ? <RegisterTab /> : <GrantRegisterTab />}
    </>
  );
}

function RegisterTab() {
  const t = useTerms();
  const register = useAsync(() => api.get<AssetsResponse>('/assets'), []);

  return (
    <AsyncPanel state={register}>
      {(data) => (
        <div className="stack">
          <div className="grid grid--3">
            <Stat
              label="Cost"
              value={money(data.totals.costMinor)}
              detail={`${data.totals.count} ${data.totals.count === 1 ? 'asset' : 'assets'} on the register`}
            />
            <Stat label="Accumulated depreciation" value={money(data.totals.depreciationMinor)} />
            <Stat label="Net book value" value={money(data.totals.nbvMinor)} detail="Cost less depreciation posted" />
          </div>

          <Card title="Asset register" flush>
            <DataTable
              caption="Fixed asset register"
              rows={data.assets}
              rowKey={(a) => a.id}
              emptyMessage="No assets are recorded."
              /*
               * Sortable, but deliberately not searchable: the total row below
               * is the whole register, and a search box would leave that total
               * sitting under a filtered handful of rows contradicting it.
               */
              columns={[
                {
                  key: 'no',
                  header: 'Asset',
                  // Wide enough for AST-2026-27-000026 on one line; a document
                  // reference broken mid-token reads as a fault.
                  width: '11rem',
                  sortValue: (a) => a.asset_no,
                  render: (a) => <span className="mono smaller">{a.asset_no}</span>,
                },
                {
                  key: 'name',
                  header: 'Description',
                  sortValue: (a) => a.name,
                  render: (a) => (
                    <>
                      {/* The title keeps a long asset name readable if the
                          column is squeezed narrow enough to clip it. */}
                      <div className="truncate" title={a.name}>
                        {a.name}
                      </div>
                      <div className="smaller muted">
                        {a.category_name}
                        {a.location_name ? ` · ${a.location_name}` : ''}
                      </div>
                    </>
                  ),
                },
                {
                  key: 'cc',
                  header: t.costCentre,
                  sortValue: (a) => a.cost_centre_code,
                  render: (a) => <span className="mono smaller">{a.cost_centre_code ?? '—'}</span>,
                },
                {
                  key: 'acquired',
                  header: 'Acquired',
                  sortValue: (a) => a.acquisition_date,
                  render: (a) => <span className="smaller">{date(a.acquisition_date)}</span>,
                },
                {
                  key: 'cost',
                  header: 'Cost',
                  numeric: true,
                  sortValue: (a) => a.current_cost_minor,
                  // Cost or valuation, which is the figure the total tile sums
                  // and the figure net book value is measured from. Showing the
                  // original acquisition cost here would stop the column adding
                  // up to its own total the first time an asset is revalued.
                  render: (a) => <Money minor={a.current_cost_minor} />,
                },
                {
                  key: 'dep',
                  header: 'Depreciation',
                  numeric: true,
                  sortValue: (a) => a.accumulated_depreciation_minor,
                  render: (a) => <Money minor={a.accumulated_depreciation_minor} blankIfZero />,
                },
                {
                  key: 'nbv',
                  header: 'Net book value',
                  numeric: true,
                  sortValue: (a) => a.net_book_value_minor,
                  render: (a) => <Money minor={a.net_book_value_minor} bold />,
                },
                {
                  key: 'life',
                  header: 'Life left',
                  numeric: true,
                  sortValue: (a) => a.remaining_life_months,
                  render: (a) =>
                    /*
                     * An asset under construction is not being depreciated at
                     * all, so its zero months left mean "not applicable" — a
                     * bare "0 months" beside a full net book value reads as a
                     * broken calculation.
                     */
                    a.remaining_life_months !== null &&
                    a.remaining_life_months > 0 &&
                    a.depreciation_method !== 'none' ? (
                      <span className="smaller">{lifeLeft(a.remaining_life_months)}</span>
                    ) : (
                      <span className="muted smaller">—</span>
                    ),
                },
                {
                  key: 'verified',
                  header: 'Last verified',
                  sortValue: (a) => a.last_verified_date,
                  render: (a) =>
                    a.last_verified_date ? (
                      <span className="smaller">{date(a.last_verified_date)}</span>
                    ) : (
                      <span className="muted smaller">Never</span>
                    ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (a) => (
                    /*
                     * Both badges on one line. Stacked, the three grant-funded
                     * rows stood taller than the twenty-three around them and
                     * the register lost its rhythm.
                     */
                    <span className="row" style={{ gap: '0.375rem', flexWrap: 'nowrap' }}>
                      <Badge tone={STATUS_TONES[a.status] ?? statusTone(a.status)}>{statusLabel(a.status)}</Badge>
                      {a.is_grant_funded ? <Badge tone="info">Grant funded</Badge> : null}
                    </span>
                  ),
                },
              ]}
              footer={
                <tr className="is-total">
                  <td colSpan={4}>Total</td>
                  <td className="num">{money(data.totals.costMinor)}</td>
                  <td className="num">{money(data.totals.depreciationMinor)}</td>
                  <td className="num">{money(data.totals.nbvMinor)}</td>
                  <td colSpan={3} />
                </tr>
              }
            />
          </Card>

          <Card
            title="Analysis by category"
            subtitle="Where the net book value of the register sits."
            flush
          >
            <DataTable
              caption="Fixed assets by category"
              rows={data.byCategory}
              rowKey={(c) => c.category}
              emptyMessage="No assets are recorded."
              columns={[
                { key: 'category', header: 'Category', render: (c) => c.category },
                { key: 'count', header: 'Assets', numeric: true, render: (c) => c.count },
                { key: 'cost', header: 'Cost', numeric: true, render: (c) => <Money minor={c.costMinor} /> },
                {
                  key: 'nbv',
                  header: 'Net book value',
                  numeric: true,
                  render: (c) => <Money minor={c.nbvMinor} bold />,
                },
              ]}
              footer={
                <tr className="is-total">
                  <td>Total</td>
                  <td className="num">{data.totals.count}</td>
                  <td className="num">{money(data.totals.costMinor)}</td>
                  <td className="num">{money(data.totals.nbvMinor)}</td>
                </tr>
              }
            />
          </Card>
        </div>
      )}
    </AsyncPanel>
  );
}

function GrantRegisterTab() {
  const grants = useAsync(() => api.get<GrantRegisterResponse>('/assets/grant-register'), []);

  return (
    <AsyncPanel state={grants}>
      {(data) => (
        <div className="stack">
          <div className="grid grid--3">
            <Stat
              label="Grants awarded"
              value={money(data.totals.awardedMinor)}
              detail={`${data.assets.length} grant-funded ${data.assets.length === 1 ? 'asset' : 'assets'}`}
            />
            <Stat
              label="Released to income"
              value={money(data.totals.releasedMinor)}
              detail="In step with depreciation"
            />
            <Stat label="Still deferred" value={money(data.totals.remainingMinor)} detail="Carried as deferred income" />
          </div>
          <Card
            title="Grant-funded assets"
            subtitle="Each grant is released to income over the life of the asset it paid for, so the income and the depreciation charge move together."
            flush
          >
            <DataTable
              caption="Deferred capital grant register"
              rows={data.assets}
              rowKey={(a) => a.id}
              emptyMessage="No assets are funded by a capital grant."
              columns={[
                {
                  key: 'no',
                  header: 'Asset',
                  width: '11rem',
                  render: (a) => <span className="mono smaller">{a.asset_no}</span>,
                },
                {
                  key: 'name',
                  header: 'Description',
                  render: (a) => (
                    <>
                      <div className="truncate" title={a.name}>
                        {a.name}
                      </div>
                      <div className="smaller muted">{a.category_name}</div>
                    </>
                  ),
                },
                { key: 'funder', header: 'Funder', render: (a) => a.grant_funder ?? '—' },
                {
                  key: 'cost',
                  header: 'Cost',
                  numeric: true,
                  render: (a) => <Money minor={Number(a.current_cost_minor)} />,
                },
                { key: 'grant', header: 'Grant', numeric: true, render: (a) => <Money minor={a.grant_amount_minor} /> },
                {
                  key: 'released',
                  header: 'Released',
                  numeric: true,
                  render: (a) => <Money minor={a.grant_released_minor} />,
                },
                {
                  key: 'remaining',
                  header: 'Deferred',
                  numeric: true,
                  render: (a) => <Money minor={a.grant_remaining_minor} bold />,
                },
              ]}
              footer={
                <tr className="is-total">
                  <td colSpan={3}>Total</td>
                  <td className="num">
                    {money(data.assets.reduce((sum, a) => sum + Number(a.current_cost_minor), 0))}
                  </td>
                  <td className="num">{money(data.totals.awardedMinor)}</td>
                  <td className="num">{money(data.totals.releasedMinor)}</td>
                  <td className="num">{money(data.totals.remainingMinor)}</td>
                </tr>
              }
            />
          </Card>
        </div>
      )}
    </AsyncPanel>
  );
}
